import {
  DefaultSchoolYear,
  EvaluationType,
  FilterKeys,
  GetAssessmentsQueryFilters,
  RepetitionType,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AssessmentRecurrenceSelect,
  AssessmentStatusSelectMultiple,
  EvaluationTypeSelectMultiple,
  FiltersSidebar,
  FiltersSidebarMethods,
  GroupSelectMultiple,
  ReportSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
  UserSelectMultiple,
} from '@schooly/components/filters';
import { AssessmentStatuses } from '@schooly/constants';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAssessmentsFilters } from './useAssessmentsFilters';

type AssessmentsFiltersSidebarProps = {
  defaultSchoolYear?: DefaultSchoolYear;
  filters: GetAssessmentsQueryFilters;
  defaultFilters: GetAssessmentsQueryFilters;
  schoolId: string;
  onApply: (v: GetAssessmentsQueryFilters) => void;
};

export const AssessmentsFiltersSidebar = forwardRef<
  FiltersSidebarMethods,
  AssessmentsFiltersSidebarProps
>(({ filters: propsFilters, defaultFilters, onApply, schoolId, defaultSchoolYear }, ref) => {
  const [filters, setFilters] = useState(propsFilters);
  const { currentStaff, permissions } = useAuth();
  const { formatMessage } = useIntl();
  const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
  const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);

  const {
    labels,
    onSetDate,
    onSetAssessmentStatus,
    onClearAssessmentStatus,
    onSetGroup,
    onClearGroup,
    onSetReport,
    onClearReport,
    onSetSubject,
    onClearSubject,
    onSetStaff,
    onClearStaff,
    onSetRecurrenceId,
    onClearRecurrenceId,
    onSetEvaluationType,
    onClearEvaluationType,
    onSetAreaOfLearning,
  } = useAssessmentsFilters({ filters, onSetFilters: setFilters });

  const handleApply = useCallback(() => {
    onApply(filters);
  }, [filters, onApply]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
    if (defaultFilters.date) {
      periodsRef.current?.updateDates(defaultFilters.date);
    }
  }, [defaultFilters]);

  const onSelectAssessmentStatus = useCallback(
    (status: AssessmentStatuses) => {
      onSetAssessmentStatus(toggleMultipleValueArrayProperty(filters.assessment_status, [status]));
    },
    [filters.assessment_status, onSetAssessmentStatus],
  );

  const onSelectGroup = useCallback(
    (groupId: string) => {
      onSetGroup(toggleMultipleValueArrayProperty(filters.group, [groupId]));
    },
    [filters.group, onSetGroup],
  );

  const onSelectReport = useCallback(
    (reportId: string) => {
      onSetReport(toggleMultipleValueArrayProperty(filters.report, [reportId]));
    },
    [filters.report, onSetReport],
  );

  const onSelectSubject = useCallback(
    (subjectId: string) => {
      onSetSubject(toggleMultipleValueArrayProperty(filters.subject, [subjectId]));
    },
    [filters.subject, onSetSubject],
  );

  const onSelectStaff = useCallback(
    (staffId: string) => {
      onSetStaff(toggleMultipleValueArrayProperty(filters.staff, [staffId]));
    },
    [filters.staff, onSetStaff],
  );

  const onSelectRecurrence = useCallback(
    (recurrenceId: string) => {
      onSetRecurrenceId([recurrenceId]);
    },
    [onSetRecurrenceId],
  );

  const onSelectEvaluationType = useCallback(
    (evaluationType: EvaluationType) => {
      onSetEvaluationType(
        toggleMultipleValueArrayProperty(filters.evaluation_type, [evaluationType]),
      );
    },
    [filters.evaluation_type, onSetEvaluationType],
  );

  const onSelectAreaOfLearning = useCallback(
    (areaOfLearningId: string) => {
      onSetAreaOfLearning(
        toggleMultipleValueArrayProperty(filters.area_of_learning, [areaOfLearningId]),
      );
    },
    [filters.area_of_learning, onSetAreaOfLearning],
  );

  const dateFilter = useMemo(() => ({ [FilterKeys.Date]: filters.date }), [filters.date]);
  const recurringFilter = useMemo(
    () => ({
      [FilterKeys.Date]: filters.date,
      [FilterKeys.RepetitionType]: [RepetitionType.FirstOfRecurrence],
    }),
    [filters.date],
  );
  return (
    <FiltersSidebar
      onOpen={() => {
        setFilters(propsFilters);
      }}
      ref={sidebar}
      title={formatMessage(
        { id: 'filter-FiltersForPage' },
        { page: formatMessage({ id: 'section-Assessments' }) },
      )}
      onClear={handleClear}
      onApply={handleApply}
    >
      <SchoolPeriodsRangeSelect
        ref={periodsRef}
        showSelectedPeriodLabel={false}
        schoolId={schoolId}
        defaultSchoolYear={defaultSchoolYear}
        hasManagePermission={permissions.includes('school_admin')}
        onSetDate={onSetDate}
        placeholder={labels[FilterKeys.Date]}
        date={filters.date || []}
        requiredLabel="required"
        onOpen={preventClose}
        onClose={allowClose}
      />

      <AssessmentStatusSelectMultiple
        placeholder={labels[FilterKeys.AssessmentStatus]}
        onSelectStatus={onSelectAssessmentStatus}
        selectedStatuses={filters.assessment_status || []}
        onClear={onClearAssessmentStatus}
        onOpen={preventClose}
        onClose={allowClose}
      />

      <GroupSelectMultiple
        placeholder={labels[FilterKeys.Group]}
        schoolId={schoolId}
        onSelectGroupId={onSelectGroup}
        selectedIds={filters.group || []}
        onClear={onClearGroup}
        filters={dateFilter}
        onOpen={preventClose}
        onClose={allowClose}
      />

      <ReportSelectMultiple
        placeholder={labels[FilterKeys.Report]}
        schoolId={schoolId}
        onSelectReportId={onSelectReport}
        selectedIds={filters.report || []}
        onClear={onClearReport}
        filters={filters}
        onOpen={preventClose}
        onClose={allowClose}
      />

      <SubjectSelectMultiple
        placeholder={labels[FilterKeys.Subject]}
        schoolId={schoolId}
        onSelectSubjectId={onSelectSubject}
        selectedIds={filters.subject || []}
        onClear={onClearSubject}
        onOpen={preventClose}
        onClose={allowClose}
      />

      <UserSelectMultiple
        type="staff"
        currentUser={currentStaff}
        placeholder={labels[FilterKeys.Staff]}
        schoolId={schoolId}
        onSelectUserId={onSelectStaff}
        selectedIds={filters.staff || []}
        onClear={onClearStaff}
        filters={dateFilter}
        onOpen={preventClose}
        onClose={allowClose}
      />

      <AssessmentRecurrenceSelect
        placeholder={labels[FilterKeys.RecurrenceId]}
        schoolId={schoolId}
        onSelectRecurrence={onSelectRecurrence}
        selectedId={filters.recurrence_id?.[0]}
        onClear={onClearRecurrenceId}
        filters={recurringFilter}
        onOpen={preventClose}
        onClose={allowClose}
      />

      <EvaluationTypeSelectMultiple
        placeholder={labels[FilterKeys.EvaluationType]}
        schoolId={schoolId}
        selectedEvaluationTypes={filters.evaluation_type || []}
        selectedAreaOfLearningIds={filters.area_of_learning || []}
        onSelectEvaluationType={onSelectEvaluationType}
        onSelectAreaOfLearning={onSelectAreaOfLearning}
        onClear={onClearEvaluationType}
        onOpen={preventClose}
        onClose={allowClose}
      />
    </FiltersSidebar>
  );
});
